import classNames from 'classnames';
import { BrandFontRef } from 'modules/BrandDefinition';
import React, { useEffect } from 'react';
import { ToolbarDropdownType } from 'components/EditorToolbar/constants';
import Icon from 'components/Icon';
import { IconType } from 'components/Icon/constants';
import { TitleWithNamespace as Title } from 'components/Toolbar/constants';
import FontFamily from 'components/Toolbar/Dropdowns/FontFamily';
import * as Models from 'models';
import { intlGet } from 'utils/intlGet';
import styles from './styles.module.scss';

const EMPTY_VALUE = '-';

type Props = {
  activeDropdown: ToolbarDropdownType | null;
  activeFont: BrandFontRef | undefined;
  returnFocusToEditor(): void;
  setFontFamily(font: Models.BrandFontMap, charStyle: Models.CharacterStyleMap): void;
  wrapperOnMouseDownHackCallback: (e: React.MouseEvent<Element>, dropDownType: ToolbarDropdownType) => void;
  closeDialog: (e: MouseEvent, type: ToolbarDropdownType, isTarget: boolean) => void;
  dataTestId?: string;
};

const FontFamilyControl: React.FunctionComponent<Props> = (props: Props) => {
  const iconRef = React.createRef<HTMLButtonElement>();
  const {
    activeDropdown,
    closeDialog,
    activeFont,
    wrapperOnMouseDownHackCallback,
    dataTestId = 'fontFamilyControl',
  } = props;
  const showDropdown = activeDropdown === ToolbarDropdownType.FONT_FAMILY;

  const handleFontSelection = (font: Models.BrandFontMap, charStyle: Models.CharacterStyleMap): void => {
    const { setFontFamily, returnFocusToEditor } = props;

    setFontFamily(font, charStyle);
    returnFocusToEditor();
  };

  useEffect(
    () => {
      const closeDialogHandler = (event: MouseEvent): void => closeDialog(
        event,
        ToolbarDropdownType.FONT_FAMILY,
        Boolean(iconRef?.current?.contains(event.target as Node)),
      );
      document.addEventListener('click', closeDialogHandler);

      return (): void => document?.removeEventListener('click', closeDialogHandler);
    },
    // iconRef should be skipped from deps
    [activeDropdown, closeDialog], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className={styles.FontFamilyControl} data-title={Title.FONT_FAMILY} data-testid={dataTestId} >
      <button
        onMouseDown={(e): void => wrapperOnMouseDownHackCallback(e, ToolbarDropdownType.FONT_FAMILY)}
        ref={iconRef}
        className={classNames(styles.fontFamilyWrapper, 'reset-button')}
        data-tooltip={activeFont?.fontName ?? intlGet('EditorToolbar.Tooltip', 'FontFamily')}
        data-testid={`${dataTestId}-button`}
      >
        <span className={styles.controlText}>{activeFont?.fontName ?? EMPTY_VALUE}</span>
        <Icon type={IconType.CHEVRON_DOWN} size="badge" color={showDropdown ? 'primary' : 'secondary'} hoverColor="primary-hover" />
      </button>
      {
        showDropdown &&
        <FontFamily
          activeFont={activeFont}
          toggleFontFamily={handleFontSelection}
          dataTestId={`${dataTestId}-fontFamily`}
        />
      }
    </div>
  );
};

export default FontFamilyControl;
